import { useEffect, useId, useReducer, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Banner, Button, Skeleton, Spinner, StatusBadge, useToast } from '@immo/ui-kit';
import type { LandIqBoardStage, LandIqBoardTicket, LandIqDataRunDetail, LandIqDataRunEntry } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { useTranslations } from '@/i18n/use-translations';
import { formatCommandLines } from '@/lib/landiq/format-command';
import { moveTicket } from '@/lib/landiq/move-ticket';
import { initRunPoll, runPollReducer } from '@/lib/landiq/run-poll';
import { AlertCircleIcon, MapIcon, PlayIcon, RefreshIcon } from '@/components/layout/icons';
import { BreadcrumbLeading } from '@/components/layout/page-leading';
import { PageTopbar } from '@/components/layout/topbar-slots';
import {
  LANDIQ_PAGE_ERROR_CODES,
  LandIqErrorState,
  type LandIqPageErrorCode,
} from '@/components/landiq/landiq-error-state';
import { moveRefusalToast, moveToastText } from '@/components/landiq/move-refusal-toast';
import { RunChunks } from '@/components/landiq/run-chunks';
import { RunFacts } from '@/components/landiq/run-facts';
import { PlannedEstimate } from '@/components/landiq/run-estimate';
import { RunLog } from '@/components/landiq/run-log';
import { RunChunkStats, RunProgress, RunTimes, SlowestChunks } from '@/components/landiq/run-metrics';
import { ResumeDataRunDialog, type ResumeOutcome } from '@/components/landiq/resume-data-run-dialog';
import { RetryFailedChunksDialog, type RetryOutcome } from '@/components/landiq/retry-failed-chunks-dialog';
import { StartDataRunDialog, type StartOutcome } from '@/components/landiq/start-data-run-dialog';
import { TicketEntryList } from '@/components/landiq/ticket-entry-list';
import { NotFoundPage } from '@/pages/not-found';
import { isSourceOutage, SOURCE_OUTAGE_TONE, STATE_TONES } from '@/lib/landiq/entry-state-tones';

/** What the page knows about its entry. LandIQ is read after the page renders, never before navigating. */
type DetailRead =
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly detail: LandIqDataRunDetail }
  | { readonly kind: 'failed'; readonly code: LandIqPageErrorCode }
  | { readonly kind: 'not-found' };

const FORBIDDEN = 403;
const ENTRY_NOT_FOUND_CODE = 'admin.landiq-data-run.not-found';
const POLL_ERROR_CODES: ReadonlyArray<string> = [
  ...LANDIQ_PAGE_ERROR_CODES,
  ENTRY_NOT_FOUND_CODE,
  'admin.landiq-data-run.log-cursor-invalid',
];

function errorCodeOf(body: unknown): string | undefined {
  return (body as { error?: { code?: string } } | undefined)?.error?.code;
}

function isLandIqPageErrorCode(code: string | undefined): code is LandIqPageErrorCode {
  return LANDIQ_PAGE_ERROR_CODES.some((candidate) => candidate === code);
}

// `not-configured` is also a 404, so the code is tested before the status or it would render as not-found.
function failureOf(result: { status: number; body: unknown }): DetailRead {
  const code = errorCodeOf(result.body);

  if (isLandIqPageErrorCode(code)) return { kind: 'failed', code };

  if (code === ENTRY_NOT_FOUND_CODE) return { kind: 'not-found' };

  // Thrown while rendering, so the route error page captures it as before.
  throw new Error(`Failed to load the LandIQ data run (${result.status})`);
}

/**
 * Reads the entry after the page has rendered, so a click on a card navigates at once and the page shows a
 * skeleton while LandIQ answers. The environment is only needed by the start confirmation: it comes from the
 * entry's own runs, else from a board read in the background that never holds the page.
 */
function useRunDetail(entryId: string, readKey: string) {
  const navigate = useNavigate();
  const [read, setRead] = useState<DetailRead>({ kind: 'loading' });
  const [boardEnvironment, setBoardEnvironment] = useState<string | null>(null);
  const [unexpected, setUnexpected] = useState<Error | null>(null);

  useEffect(() => {
    let current = true;
    setRead({ kind: 'loading' });

    void (async () => {
      try {
        const detail = await apiClient.adminLandiqDataRuns.get({ pathParams: { entryId }, query: {} });

        if (!current) return;

        if (!detail.ok && detail.status === FORBIDDEN) {
          navigate('/access-denied', { replace: true, state: { reason: 'missing-permission' } });

          return;
        }

        setRead(detail.ok ? { kind: 'loaded', detail: detail.body } : failureOf(detail));
      } catch (error) {
        if (current) setUnexpected(error instanceof Error ? error : new Error(String(error)));
      }
    })();

    void (async () => {
      try {
        const board = await apiClient.adminLandiqDataRuns.list();

        if (current && board.ok) setBoardEnvironment(board.body.environment);
      } catch {
        // The environment falls back to the entry's runs; the board read is only a fallback for an entry that never ran.
      }
    })();

    return () => {
      current = false;
    };
  }, [entryId, readKey, navigate]);

  if (unexpected !== null) throw unexpected;

  return { read, boardEnvironment };
}

export function LandIqDataRunDetailPage() {
  const { entryId = '' } = useParams();
  const location = useLocation();
  const { read, boardEnvironment } = useRunDetail(entryId, location.key);

  if (read.kind === 'not-found') return <NotFoundPage />;

  if (read.kind === 'failed') {
    return (
      <>
        <PageTopbar leading={<Crumbs entryId={entryId} />} />
        <LandIqErrorState code={read.code} />
      </>
    );
  }

  if (read.kind === 'loading') return <RunSkeleton entryId={entryId} />;

  const environment = read.detail.run?.environment ?? read.detail.entry.lastRun?.environment ?? boardEnvironment;

  return <RunView key={location.key} entryId={entryId} detail={read.detail} environment={environment} />;
}

/** The page frame and the shape of the run, shown the moment the card is clicked. */
function RunSkeleton({ entryId }: { readonly entryId: string }) {
  return (
    <>
      <PageTopbar leading={<Crumbs entryId={entryId} />} />
      <div className="h-full min-h-0 overflow-y-auto bg-canvas">
        <div className="flex max-w-[1040px] flex-col gap-6 p-6" aria-busy="true">
          <header className="flex flex-col gap-2">
            <h1 className="break-words font-mono text-lg font-semibold text-text-primary">{entryId}</h1>
            <Skeleton className="h-5 w-48" />
          </header>
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    </>
  );
}

function Crumbs({ entryId }: { readonly entryId: string }) {
  const t = useTranslations('pages.admin.landiq-data-run');

  return (
    <BreadcrumbLeading
      crumbs={[{ label: t('breadcrumb'), to: '/landiq/data-runs' }, { label: entryId }]}
      icon={MapIcon}
    />
  );
}

function RunView({
  entryId,
  detail,
  environment,
}: {
  readonly entryId: string;
  readonly detail: LandIqDataRunDetail;
  readonly environment: string | null;
}) {
  const t = useTranslations('pages.admin.landiq-data-run');
  const tList = useTranslations('pages.admin.landiq-data-runs');
  const tErrors = useTranslations('errors');
  const [state, dispatch] = useReducer(runPollReducer, detail, initRunPoll);
  const [startingEntry, setStartingEntry] = useState<LandIqDataRunEntry | null>(null);
  const [resumingEntry, setResumingEntry] = useState<LandIqDataRunEntry | null>(null);
  const toast = useToast();
  const [moving, setMoving] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [movedFrom, setMovedFrom] = useState<LandIqDataRunDetail | null>(null);
  const latestDetail = useRef(state.detail);
  const { next } = state;
  const polling = next !== null;
  const { entry, run, progress, ticket, canRetryFailedChunks, canResume, slowestChunks } = state.detail;
  const failedChunks = progress?.chunks?.failed ?? 0;

  useEffect(() => {
    latestDetail.current = state.detail;
  }, [state.detail]);

  useEffect(() => {
    if (next === null) return undefined;

    let current = true;
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const result = await apiClient.adminLandiqDataRuns.get({
            pathParams: { entryId },
            query: next.logAfter === null ? {} : { logAfter: next.logAfter },
          });

          if (!current) return;

          if (result.ok) dispatch({ type: 'answered', read: next, answer: result.body });
          else dispatch({ type: 'failed', code: errorCodeOf(result.body) });
        } catch {
          if (current) dispatch({ type: 'failed', code: undefined });
        }
      })();
    }, next.delay);

    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [entryId, next]);

  const handleSettled = (outcome: StartOutcome) => {
    if (outcome.kind === 'started') setStartingEntry(null);

    dispatch({ type: 'reread' });
  };

  const handleResumeSettled = (outcome: ResumeOutcome) => {
    if (outcome.kind === 'started') setResumingEntry(null);

    dispatch({ type: 'reread' });
  };

  const handleRetrySettled = (outcome: RetryOutcome) => {
    if (outcome.kind === 'retried') {
      setRetrying(false);
      toast.show('success', t('retry-failed-chunks-dialog.retried', { count: outcome.movedChunks }));
    }

    dispatch({ type: 'reread' });
  };

  async function handleMove(to: LandIqBoardStage) {
    setMoving(true);
    const outcome = await moveTicket(ticket.ticket, to);
    setMoving(false);

    if (outcome.kind === 'moved') {
      setMovedFrom(latestDetail.current);
    } else if (outcome.kind === 'refused') {
      const moveToast = moveRefusalToast(outcome.refusal, {
        stageEntries: ticket.entries.filter((e) => e.stage === to).length,
        ownColumn: ticket.column,
      });
      toast.show(moveToast.variant, moveToastText(tList, moveToast));
    } else {
      toast.show('danger', tErrors(isLandIqPageErrorCode(outcome.code) ? outcome.code : 'generic.unexpected'));
    }

    dispatch({ type: 'reread' });
  }

  const failureCode = state.failure?.code;
  const failureTitle = tErrors(
    failureCode !== undefined && POLL_ERROR_CODES.includes(failureCode) ? failureCode : 'generic.unexpected',
  );

  return (
    <>
      <PageTopbar leading={<Crumbs entryId={entryId} />} />
      <div className="h-full min-h-0 overflow-y-auto bg-canvas">
        <div className="flex max-w-[1040px] flex-col gap-6 p-6">
          {state.failure !== null && <Banner variant="danger" icon={<AlertCircleIcon />} title={failureTitle} />}
          <header className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 flex-col gap-2">
              <h1 className="break-words font-mono text-lg font-semibold text-text-primary">{entry.id}</h1>
              <div className="flex items-center gap-3" aria-busy={polling}>
                {isSourceOutage(entry.state, (run ?? entry.lastRun)?.failureReason) ? (
                  <StatusBadge label={tList('failure-reasons.SOURCE_OUTAGE')} tone={SOURCE_OUTAGE_TONE} size="sm" />
                ) : (
                  <StatusBadge label={tList(`states.${entry.state}`)} tone={STATE_TONES[entry.state]} size="sm" />
                )}
                {polling && <Spinner size="sm" className="text-info-700" />}
                <span className="font-mono text-sm text-text-tertiary">{entry.ticket}</span>
                {entry.state === 'BLOCKED' && (
                  <span className="text-sm text-warning-700">
                    {tList('waits-for', { entries: entry.blockedBy.join(', ') })}
                  </span>
                )}
              </div>
            </div>
            <HeaderActions
              entry={entry}
              ticket={ticket}
              showMove={state.detail !== movedFrom}
              moving={moving}
              canRetryFailedChunks={canRetryFailedChunks}
              failedChunks={failedChunks}
              onRetryFailedChunks={() => setRetrying(true)}
              canResume={canResume}
              onResume={() => setResumingEntry(entry)}
              onStart={setStartingEntry}
              onMove={(to) => void handleMove(to)}
            />
          </header>
          <section className="flex flex-col gap-2">
            <h2 className="text-sm font-semibold text-text-secondary">{t('sections.command')}</h2>
            <div className="rounded-md border border-border bg-canvas px-3 py-2 font-mono text-sm text-text-primary">
              {formatCommandLines(entry.command, entry.args).map((line, index) => (
                <div key={`${index}:${line}`} className={index === 0 ? undefined : 'pl-4'}>
                  {line}
                </div>
              ))}
            </div>
          </section>
          {run === null ? (
            <div className="rounded-md border border-dashed border-border-strong p-4 text-text-secondary">
              {t('never-ran')}
              {entry.estimateMinutes !== null && (
                <>
                  {' '}
                  <PlannedEstimate minutes={entry.estimateMinutes} className="tabular-nums text-text-tertiary" />
                </>
              )}
            </div>
          ) : (
            <>
              <RunFacts run={run} progress={progress} />
              {entry.completion === 'IMPORT' && progress?.chunks && (
                <RunChunks chunks={progress.chunks} queueDepth={progress.queueDepth} />
              )}
              {run.commandProgress !== null && (
                <RunProgress progress={run.commandProgress} running={run.state === 'RUNNING'} />
              )}
              {run.timings !== null && <RunTimes timings={run.timings} />}
              {run.chunkStats !== null && <RunChunkStats stats={run.chunkStats} />}
              {slowestChunks.length > 0 && <SlowestChunks chunks={slowestChunks} />}
            </>
          )}
          {ticket.entries.length > 1 && (
            <section className="flex flex-col gap-2">
              <h2 className="text-sm font-semibold text-text-secondary">
                {t('sections.entries', { ticket: ticket.ticket })}
              </h2>
              <TicketEntryList
                ticket={ticket}
                currentEntryId={entry.id}
                className="rounded-md border border-border bg-surface px-3 py-1"
              />
            </section>
          )}
          {run !== null && (
            <section className="flex flex-col gap-2">
              <h2 className="text-sm font-semibold text-text-secondary">{t('sections.log')}</h2>
              <RunLog renderJobId={run.renderJobId} lines={state.lines} />
            </section>
          )}
        </div>
      </div>
      {/* Opens once the environment is known — from the entry's runs at once, else from the background board read. */}
      {startingEntry !== null && environment !== null && (
        <StartDataRunDialog
          entry={startingEntry}
          environment={environment}
          onClose={() => setStartingEntry(null)}
          onSettled={handleSettled}
        />
      )}
      {resumingEntry !== null && environment !== null && (
        <ResumeDataRunDialog
          entry={resumingEntry}
          environment={environment}
          onClose={() => setResumingEntry(null)}
          onSettled={handleResumeSettled}
        />
      )}
      {retrying && (
        <RetryFailedChunksDialog
          entryId={entry.id}
          count={failedChunks}
          onClose={() => setRetrying(false)}
          onSettled={handleRetrySettled}
        />
      )}
    </>
  );
}

function HeaderActions({
  entry,
  ticket,
  showMove,
  moving,
  canRetryFailedChunks,
  failedChunks,
  onRetryFailedChunks,
  canResume,
  onResume,
  onStart,
  onMove,
}: {
  readonly entry: LandIqDataRunEntry;
  readonly ticket: LandIqBoardTicket;
  readonly showMove: boolean;
  readonly moving: boolean;
  readonly canRetryFailedChunks: boolean;
  readonly failedChunks: number;
  readonly onRetryFailedChunks: () => void;
  readonly canResume: boolean;
  readonly onResume: () => void;
  readonly onStart: (entry: LandIqDataRunEntry) => void;
  readonly onMove: (to: LandIqBoardStage) => void;
}) {
  const hintId = useId();
  const t = useTranslations('pages.admin.landiq-data-run');
  const tList = useTranslations('pages.admin.landiq-data-runs');
  const moveTo = showMove ? ticket.next : null;
  const canStart = ticket.next === null && (entry.state === 'PENDING' || entry.state === 'BLOCKED');
  const fullReuploadHint =
    entry.state === 'FAILED' && ticket.entries.find((candidate) => candidate.id === entry.id)?.stage === 'UPLOAD';

  if (moveTo === null && entry.state !== 'FAILED' && !canStart && !canRetryFailedChunks && !canResume) return null;

  return (
    <div className="flex shrink-0 items-center gap-2">
      {moveTo !== null && (
        <Button variant="primary" loading={moving} disabled={moving} onClick={() => onMove(moveTo)}>
          <PlayIcon aria-hidden className="h-4 w-4" />
          {tList(`board.columns.${moveTo}`)}
        </Button>
      )}
      {canRetryFailedChunks && (
        <Button variant="secondary" onClick={onRetryFailedChunks}>
          <RefreshIcon aria-hidden className="h-4 w-4" />
          {t('actions.retry-failed-chunks', { count: failedChunks })}
        </Button>
      )}
      {canResume && (
        <Button variant="secondary" onClick={onResume}>
          <PlayIcon aria-hidden className="h-4 w-4" />
          {t('actions.resume')}
        </Button>
      )}
      {entry.state === 'FAILED' && (
        <>
          <Button
            variant="secondary"
            aria-describedby={fullReuploadHint ? hintId : undefined}
            onClick={() => onStart(entry)}
          >
            <RefreshIcon aria-hidden className="h-4 w-4" />
            {t('actions.retry')}
          </Button>
          {fullReuploadHint && (
            <span id={hintId} className="text-xs text-text-tertiary">
              {t('retry-hint')}
            </span>
          )}
        </>
      )}
      {canStart && (
        <Button variant="primary" disabled={entry.state === 'BLOCKED'} onClick={() => onStart(entry)}>
          <PlayIcon aria-hidden className="h-4 w-4" />
          {t('actions.start')}
        </Button>
      )}
    </div>
  );
}
